import {
  AlignItems,
  AlertCard,
  AlertCardContent,
  AlertCardVariant,
  Body2,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  GridDisplay,
  Icon,
  IconVariant,
  JustifyContent,
  Select,
  Spinner,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import {
  type DashboardExperienceOrderDeliveryDetailsDay,
  type DashboardExperienceOrderDeliveryDetailsResponse,
} from '@library/api-dashboard-experience'
import React, { useEffect } from 'react'

const DeliveryDateSelect = withCitrusTheme(Select, () => ({
  backgroundColor: '#FFFFFF',
}))

type ManageDeliveryPanelContentProps = {
  currentOrderDeliveryDate: string
  currentOrderDeliveryDateLabel?: string
  hasSaveDeliveryDateError?: boolean
  hasSkipOrderError?: boolean
  isSavingDeliveryDate?: boolean
  isSkipBoxDisabled?: boolean
  isSkipOrder?: boolean
  onSaveDeliveryDate?: (selection: { date: string; formattedDate: string }) => void | Promise<void>
  onSkipBoxClick?: () => void
  onTrackBoxClick?: (url: string) => void
  orderDeliveryDetails: DashboardExperienceOrderDeliveryDetailsResponse | undefined
  orderState?: string
}

const SAVE_DELIVERY_DATE_ERROR_MESSAGE =
  "We couldn't update your delivery day: Please try again. If it keeps happening, contact customer support for help."
const SKIP_ORDER_ERROR_MESSAGE =
  "We couldn't skip this order: Something went wrong, please try again. If it keeps happening, contact customer support."

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const MONTHS_BY_NAME: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
}

const getOrdinalDay = (day: number): string => {
  const remainder = day % 10
  const teenRemainder = day % 100

  if (teenRemainder >= 11 && teenRemainder <= 13) {
    return `${day}th`
  }

  if (remainder === 1) {
    return `${day}st`
  }

  if (remainder === 2) {
    return `${day}nd`
  }

  if (remainder === 3) {
    return `${day}rd`
  }

  return `${day}th`
}

const getNormalisedDeliveryDateParts = (
  deliveryDate: string,
): { year: number; month: number; day: number } | null => {
  const isoDateMatch = deliveryDate.match(/^(\d{4})-(\d{2})-(\d{2})/)

  if (!isoDateMatch) {
    return null
  }

  const [, year, month, day] = isoDateMatch

  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  }
}

const getNormalisedDeliveryDateLabelKey = (deliveryDateLabel: string): string => {
  const trimmedLabel = deliveryDateLabel.trim()
  const match = trimmedLabel.match(
    /^(?:[A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]+)(?:\s+\d{4})?$/,
  )

  if (!match) {
    return trimmedLabel.toLowerCase()
  }

  const [, day, monthName] = match
  const month = MONTHS_BY_NAME[monthName.toLowerCase()]

  if (!month) {
    return trimmedLabel.toLowerCase()
  }

  return `${month.toString().padStart(2, '0')}-${day.padStart(2, '0')}`
}

const getDeliveryDateYear = (deliveryDate: string): number | null => {
  const normalisedDateParts = getNormalisedDeliveryDateParts(deliveryDate)

  return normalisedDateParts?.year ?? null
}

const getIsoDeliveryDateFromLabel = (
  deliveryDateLabel: string,
  fallbackYear: number | null,
): string | null => {
  const trimmedLabel = deliveryDateLabel.trim()
  const match = trimmedLabel.match(
    /^(?:[A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]+)(?:\s+(\d{4}))?$/,
  )

  if (!match) {
    return null
  }

  const [, day, monthName, explicitYear] = match
  const month = MONTHS_BY_NAME[monthName.toLowerCase()]
  const year = explicitYear ? Number(explicitYear) : fallbackYear

  if (!month || !year) {
    return null
  }

  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.padStart(
    2,
    '0',
  )}`
}

const getDeliveryDateKey = (deliveryDate: string): string => {
  const normalisedDateParts = getNormalisedDeliveryDateParts(deliveryDate)

  if (!normalisedDateParts) {
    return deliveryDate
  }

  const { year, month, day } = normalisedDateParts

  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`
}

const formatDeliveryDate = (deliveryDate: string): string => {
  const normalisedDateParts = getNormalisedDeliveryDateParts(deliveryDate)

  if (!normalisedDateParts) {
    return deliveryDate
  }

  const { year, month, day } = normalisedDateParts
  const parsedDate = new Date(Date.UTC(year, month - 1, day))

  return `${WEEKDAY_NAMES[parsedDate.getUTCDay()]} ${getOrdinalDay(day)} ${MONTH_NAMES[month - 1]}`
}

const ManageDeliveryErrorCard = ({ message }: { message: string }) => (
  <AlertCard data-testid="alert-card" variant={AlertCardVariant.Warning}>
    <AlertCardContent>{message}</AlertCardContent>
  </AlertCard>
)

const ManageDeliveryLoadingState = () => (
  <Box
    alignItems={AlignItems.Center}
    data-testid="manage-delivery-loading-spinner"
    display={Display.Flex}
    gridColumn="1 / -1"
    justifyContent={JustifyContent.Center}
    minHeight="160px"
    role="status"
    aria-label="Loading delivery details"
  >
    <Spinner size={6} />
  </Box>
)

const SkipOrderInfoBanner = () => (
  <Box
    bg={Color.Informative_50}
    borderColor={Color.Informative_200}
    borderWidth={0.5}
    borderStyle={BorderStyle.Solid}
    paddingH={3}
    paddingV={3}
    data-testid="skip-order-info-banner"
    style={{ borderRadius: '8px' }}
  >
    <Box display={Display.Flex} alignItems={AlignItems.FlexStart}>
      <Box color={Color.Informative_700} display={Display.Flex}>
        <Icon name="info" variant={IconVariant.Informative} />
      </Box>
      <Space size={2} direction="horizontal" />
      <Body2 color={Color.ColdGrey_800}>{"'Restore order' to edit your delivery date"}</Body2>
    </Box>
  </Box>
)

export function ManageDeliveryPanelContent({
  currentOrderDeliveryDate,
  currentOrderDeliveryDateLabel,
  hasSaveDeliveryDateError = false,
  hasSkipOrderError = false,
  isSavingDeliveryDate = false,
  isSkipBoxDisabled = false,
  isSkipOrder = false,
  onSaveDeliveryDate,
  onSkipBoxClick = () => undefined,
  onTrackBoxClick = () => undefined,
  orderDeliveryDetails,
  orderState = '',
}: ManageDeliveryPanelContentProps) {
  const availableDeliveryDays = orderDeliveryDetails?.available_delivery_days
  const trackingLinks = (orderDeliveryDetails?.tracking ?? []).reduce<
    { title: string; url: string }[]
  >((validTrackingLinks, trackingLink) => {
    const title = typeof trackingLink.title === 'string' ? trackingLink.title.trim() : ''
    const url = typeof trackingLink.url === 'string' ? trackingLink.url.trim() : ''

    return title && url ? [...validTrackingLinks, { title, url }] : validTrackingLinks
  }, [])

  const baseAvailableDeliveryDayOptions: DashboardExperienceOrderDeliveryDetailsDay[] =
    availableDeliveryDays
      ? Object.keys(availableDeliveryDays).map(
          (deliveryDate) => availableDeliveryDays[deliveryDate],
        )
      : []
  const shouldRenderDeliveryDateSelect =
    !isSkipOrder && availableDeliveryDays !== null && availableDeliveryDays !== undefined
  const shouldShowSkipBoxButton =
    !isSkipOrder && (orderState === 'choose' || orderState === 'edit' || orderState === 'menuOpen')
  const shouldShowTrackBoxButtons = trackingLinks.length > 0

  const matchedCurrentDeliveryDayOptionFromOrderTitle = baseAvailableDeliveryDayOptions.find(
    (deliveryDay) =>
      getNormalisedDeliveryDateLabelKey(deliveryDay.formatted_date) ===
      getNormalisedDeliveryDateLabelKey(currentOrderDeliveryDateLabel || ''),
  )

  const matchedCurrentDeliveryDayOptionFromOrderDate = baseAvailableDeliveryDayOptions.find(
    (deliveryDay) =>
      getDeliveryDateKey(deliveryDay.date) === getDeliveryDateKey(currentOrderDeliveryDate),
  )

  const matchedCurrentDeliveryDayOption = baseAvailableDeliveryDayOptions.find(
    (deliveryDay) =>
      getNormalisedDeliveryDateLabelKey(deliveryDay.formatted_date) ===
      getNormalisedDeliveryDateLabelKey(orderDeliveryDetails?.delivery_date || ''),
  )

  const currentOrderDeliveryDateFromLabel = getIsoDeliveryDateFromLabel(
    orderDeliveryDetails?.delivery_date || '',
    getDeliveryDateYear(currentOrderDeliveryDate),
  )

  const currentOrderDeliveryDateFromOrderTitle = getIsoDeliveryDateFromLabel(
    currentOrderDeliveryDateLabel || '',
    getDeliveryDateYear(currentOrderDeliveryDate),
  )

  const resolvedCurrentOrderDeliveryDate =
    matchedCurrentDeliveryDayOptionFromOrderTitle?.date ||
    matchedCurrentDeliveryDayOptionFromOrderDate?.date ||
    currentOrderDeliveryDateFromOrderTitle ||
    matchedCurrentDeliveryDayOption?.date ||
    currentOrderDeliveryDateFromLabel ||
    getDeliveryDateKey(currentOrderDeliveryDate)

  const shouldAppendCurrentDeliveryDateOption =
    shouldRenderDeliveryDateSelect &&
    !!resolvedCurrentOrderDeliveryDate &&
    !baseAvailableDeliveryDayOptions.some(
      (deliveryDay) =>
        getDeliveryDateKey(deliveryDay.date) ===
        getDeliveryDateKey(resolvedCurrentOrderDeliveryDate),
    )

  const availableDeliveryDayOptions = shouldAppendCurrentDeliveryDateOption
    ? [
        ...baseAvailableDeliveryDayOptions,
        {
          id: 'current-order-delivery-day',
          date: resolvedCurrentOrderDeliveryDate,
          formatted_date: formatDeliveryDate(resolvedCurrentOrderDeliveryDate),
          isDefault: false,
          coreDayId: '',
          unavailableReason: '',
          alternateDeliveryDay: null,
          slots: [],
        },
      ]
    : baseAvailableDeliveryDayOptions

  const initialSelectedDate = availableDeliveryDayOptions.some(
    (deliveryDay) =>
      getDeliveryDateKey(deliveryDay.date) === getDeliveryDateKey(resolvedCurrentOrderDeliveryDate),
  )
    ? resolvedCurrentOrderDeliveryDate
    : availableDeliveryDayOptions[0]?.date ?? ''

  const [selectedDate, setSelectedDate] = React.useState(initialSelectedDate)

  useEffect(() => {
    setSelectedDate(initialSelectedDate)
  }, [initialSelectedDate])

  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDate(e.target.value)
  }

  const selectedDeliveryDay = availableDeliveryDayOptions.find(
    (deliveryDay) => getDeliveryDateKey(deliveryDay.date) === getDeliveryDateKey(selectedDate),
  )

  const isSaveDisabled =
    !selectedDate ||
    getDeliveryDateKey(selectedDate) === getDeliveryDateKey(resolvedCurrentOrderDeliveryDate)
  const mobileAddressGridRow = shouldRenderDeliveryDateSelect ? '3' : '2'
  const mobileBoxSizeGridRow = shouldRenderDeliveryDateSelect ? '4' : '3'
  const mobileTrackBoxGridRow = shouldRenderDeliveryDateSelect ? '5' : '4'
  const mobileSkipBoxGridRow = shouldRenderDeliveryDateSelect
    ? shouldShowTrackBoxButtons
      ? '6'
      : '5'
    : shouldShowTrackBoxButtons
    ? '5'
    : '4'
  const desktopTrackBoxGridRow = shouldRenderDeliveryDateSelect ? '3' : '2'

  if (!orderDeliveryDetails) {
    return (
      <Box
        display={GridDisplay.Grid}
        gridTemplateColumns={['1fr', '1fr', 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)']}
        gap={6}
      >
        <ManageDeliveryLoadingState />
      </Box>
    )
  }

  return (
    <Box
      display={GridDisplay.Grid}
      gridTemplateColumns={['1fr', '1fr', 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)']}
      gap={6}
    >
      <Box gridColumn={['1 / -1', '1 / -1', '1 / 2']} gridRow="1">
        <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold}>
          Delivery date
        </Body2>
        <Space size={1} direction="vertical" />
        {shouldRenderDeliveryDateSelect ? (
          <DeliveryDateSelect
            aria-label="Delivery date"
            data-testid="delivery-date-select"
            fullWidth
            key={initialSelectedDate}
            defaultValue={initialSelectedDate}
            onChange={handleDateChange}
          >
            {availableDeliveryDayOptions.map((availableDeliveryDay) => (
              <option key={availableDeliveryDay.id} value={availableDeliveryDay.date}>
                {availableDeliveryDay.formatted_date}
              </option>
            ))}
          </DeliveryDateSelect>
        ) : (
          <Body2 color={Color.ColdGrey_800}>
            {orderDeliveryDetails?.delivery_date ?? 'Loading...'}
          </Body2>
        )}
        {isSkipOrder ? (
          <>
            <Space size={2} direction="vertical" />
            <SkipOrderInfoBanner />
          </>
        ) : null}
      </Box>
      {shouldRenderDeliveryDateSelect ? (
        <Box
          gridColumn={['1 / -1', '1 / -1', '1 / 2']}
          gridRow="2"
          data-testid="save-delivery-date-action"
        >
          <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
            <Button
              disabled={isSaveDisabled || isSavingDeliveryDate}
              width="100%"
              onClick={() => {
                if (selectedDeliveryDay) {
                  void onSaveDeliveryDate?.({
                    date: selectedDeliveryDay.date,
                    formattedDate: selectedDeliveryDay.formatted_date,
                  })
                }
              }}
            >
              {isSavingDeliveryDate ? (
                <Spinner color={Color.White} data-testid="save-delivery-date-spinner" size={6} />
              ) : (
                'Save delivery date'
              )}
            </Button>
            {hasSaveDeliveryDateError ? (
              <ManageDeliveryErrorCard message={SAVE_DELIVERY_DATE_ERROR_MESSAGE} />
            ) : null}
          </Box>
        </Box>
      ) : null}
      <Box
        gridColumn={['1 / -1', '1 / -1', '2 / 3']}
        gridRow={[mobileAddressGridRow, mobileAddressGridRow, '1']}
      >
        <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold}>
          Delivery address
        </Body2>
        <Space size={1} direction="vertical" />
        {orderDeliveryDetails?.delivery_address ? (
          <>
            <Body2 color={Color.ColdGrey_800}>
              {orderDeliveryDetails.delivery_address.first_line}
            </Body2>
            <Body2 color={Color.ColdGrey_800}>
              {orderDeliveryDetails.delivery_address.second_line}
            </Body2>
            <Body2 color={Color.ColdGrey_800}>
              {orderDeliveryDetails.delivery_address.postcode}
            </Body2>
          </>
        ) : (
          <Body2 color={Color.ColdGrey_800}>Loading...</Body2>
        )}
      </Box>
      <Box
        gridColumn={['1 / -1', '1 / -1', '3 / 4']}
        gridRow={[mobileBoxSizeGridRow, mobileBoxSizeGridRow, '1']}
      >
        <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold}>
          Box size
        </Body2>
        <Space size={1} direction="vertical" />
        <Body2 color={Color.ColdGrey_800}>{orderDeliveryDetails?.box_size ?? 'Loading...'}</Body2>
      </Box>
      {shouldShowTrackBoxButtons ? (
        <Box
          gridColumn={['1 / -1', '1 / -1', '1 / 2']}
          gridRow={[mobileTrackBoxGridRow, mobileTrackBoxGridRow, desktopTrackBoxGridRow]}
          data-testid="track-box-actions"
        >
          <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
            {trackingLinks.map(({ title, url }, index) => (
              <Button
                colorVariant={ButtonColorVariant.Secondary}
                key={`${url}-${index}`}
                width="100%"
                onClick={() => onTrackBoxClick(url)}
              >
                {title}
              </Button>
            ))}
          </Box>
        </Box>
      ) : null}
      {shouldShowSkipBoxButton ? (
        <Box
          gridColumn={['1 / -1', '1 / -1', '2 / -1']}
          gridRow={[mobileSkipBoxGridRow, mobileSkipBoxGridRow, '2']}
          data-testid="skip-box-action"
        >
          {isSkipBoxDisabled ? (
            <Box
              width="100%"
              minHeight="48px"
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
              gap={2}
              bg={Color.ColdGrey_50}
              borderColor={Color.ColdGrey_100}
              borderWidth={0.5}
              borderStyle={BorderStyle.Solid}
              borderRadius={1}
              data-testid="skip-order-loading-placeholder"
            >
              <Spinner size={6} />
              <Body2 color={Color.ColdGrey_800} fontWeight={FontWeight.Bold}>
                Skipping order...
              </Body2>
            </Box>
          ) : (
            <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
              <Button
                colorVariant={ButtonColorVariant.Secondary}
                width="100%"
                disabled={isSkipBoxDisabled}
                onClick={onSkipBoxClick}
              >
                Skip box
              </Button>
              {hasSkipOrderError ? (
                <ManageDeliveryErrorCard message={SKIP_ORDER_ERROR_MESSAGE} />
              ) : null}
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  )
}
